import { Box, IconButton } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { mergeAttributes, Node, NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

interface VideoOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      setVideo: (options: { src: string; width?: number; height?: number }) => ReturnType;
    };
  }
}

const VideoComponent = ({ node, deleteNode }: NodeViewProps) => {
  const { src, width, height } = node.attrs;

  return (
    <NodeViewWrapper className="video-wrapper">
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          margin: '1rem 0',
        }}
      >
        <video
          src={src}
          width={width}
          height={height}
          controls
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
          }}
        />
        <IconButton
          onClick={deleteNode}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
            },
          }}
          size="small"
        >
          <XIcon size={16} />
        </IconButton>
      </Box>
    </NodeViewWrapper>
  );
};

export const Video = Node.create<VideoOptions>({
  name: 'video',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { controls: true })];
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
          ({ commands }) => {
            return commands.insertContent({
              type: this.name,
              attrs: options,
            });
          },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoComponent);
  },
});

export default Video; 